import { fromOptionalSelectValue, OPTIONAL_SELECT_NONE, toOptionalSelectValue } from "@structura/core/optional-select";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ClinicalPageShell } from "@/components/clinical";
import { DataTable, EmptyState, Loading } from "@/components/ds";
import { CrudShellHeader } from "@/components/ds/CrudShell";
import type { DataTableColumn } from "@/components/ds";
import { ImageUploadField } from "@/components/ImageUploadField";
import { SmartMatchChip } from "@/components/media/SmartMatchChip";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DEFAULT_CATEGORY_EXPERIENCE,
  businessProfileSupportsCategoryAccent,
  businessProfileUsesCategoryNavigation,
  normalizeCategoryExperience,
  type CategoryExperience,
  type CategoryExperienceBadge,
} from "@/lib/category-experience";
import { useTenantContext } from "@/lib/tenant-context";
import { CrudEditorShell } from "@/components/workspace/CrudEditorShell";
import { FieldInlineHelp, FieldLabel } from "@/components/help/FieldHelpTip";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Download, Loader2, Pencil, Plus, Tag, Trash2 } from "lucide-react";
import { apiGetJson, apiSendJson, apiDelete, clientRequestHeaders } from "@/lib/sm-api";
import { RenataLauncher } from "@/components/renata/RenataLauncher";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import { toast } from "sonner";

export const Route = createFileRoute("/app/categorias")({
  component: CategoriasPage,
  head: () => ({ meta: [{ title: "Categorías — Structura Market" }] }),
});

interface Categoria {
  id: string;
  name: string;
  slug: string;
  parentId: string | null;
  parentName?: string | null;
  description: string | null;
  imageUrl: string | null;
  sortOrder: number;
  active: boolean;
  createdAt: string;
  experience?: CategoryExperience;
}

type CategoryFormState = {
  name: string;
  description: string;
  imageUrl: string;
  sortOrder: number;
  parentId: string;
  experience: CategoryExperience;
};

function emptyForm(): CategoryFormState {
  return {
    name: "",
    description: "",
    imageUrl: "",
    sortOrder: 0,
    parentId: "",
    experience: {
      ...DEFAULT_CATEGORY_EXPERIENCE,
      nav: { ...DEFAULT_CATEGORY_EXPERIENCE.nav },
    },
  };
}

function CategoriasPage() {
  const tid = useWorkspaceTenantId();
  const { businessProfile } = useTenantContext();
  const showCategoryExperience = businessProfileUsesCategoryNavigation(businessProfile);
  const showAccent = businessProfileSupportsCategoryAccent(businessProfile);

  const [cats, setCats]               = useState<Categoria[]>([]);
  const [loading, setLoading]         = useState(true);
  const [saving, setSaving]           = useState(false);
  const [editorOpen, setEditorOpen]   = useState(false);
  const [editing, setEditing]         = useState<Categoria | null>(null);
  const [form, setForm]               = useState(emptyForm);
  const [deleteTarget, setDeleteTarget] = useState<Categoria | null>(null);

  const load = () => {
    if (!tid) return;
    setLoading(true);
    apiGetJson<Categoria[]>("/api/categories", tid)
      .then((list) => {
        const byId = new Map(list.map((c) => [c.id, c]));
        setCats(
          list.map((c) => ({
            ...c,
            parentName: c.parentId ? byId.get(c.parentId)?.name ?? null : null,
          })),
        );
      })
      .catch(() => toast.error("Error al cargar categorías"))
      .finally(() => setLoading(false));
  };

  useEffect(load, [tid]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setEditorOpen(true);
  };

  const openEdit = (cat: Categoria) => {
    setEditing(cat);
    setForm({
      name:        cat.name,
      description: cat.description ?? "",
      imageUrl:    cat.imageUrl ?? "",
      sortOrder:   cat.sortOrder,
      parentId:    cat.parentId ?? "",
      experience:  normalizeCategoryExperience(cat.experience, cat.sortOrder),
    });
    setEditorOpen(true);
  };

  const handleSave = async () => {
    if (!tid) return;
    if (!form.name.trim()) { toast.error("El nombre es requerido"); return; }
    const nameKey = form.name.trim().toLowerCase();
    const duplicate = cats.some(
      (c) => c.name.trim().toLowerCase() === nameKey && c.id !== editing?.id,
    );
    if (duplicate) {
      toast.error("Ya existe una categoría con ese nombre");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await apiSendJson(`/api/categories?id=${editing.id}`, "PATCH", {
          name:        form.name.trim(),
          description: form.description.trim() || null,
          imageUrl:    form.imageUrl.trim() || null,
          sortOrder:   form.sortOrder,
          parentId:    form.parentId || null,
          experience:  form.experience,
        }, tid);
        toast.success("Categoría actualizada");
      } else {
        await apiSendJson("/api/categories", "POST", {
          name:        form.name.trim(),
          description: form.description.trim() || null,
          imageUrl:    form.imageUrl.trim() || null,
          sortOrder:   form.sortOrder,
          experience:  form.experience,
          parentId:    form.parentId || null,
        }, tid);
        toast.success("Categoría creada");
      }
      setEditorOpen(false);
      load();
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (cat: Categoria) => {
    if (!tid) return;
    try {
      await apiSendJson(`/api/categories?id=${cat.id}`, "PATCH", { active: !cat.active }, tid);
      setCats(prev => prev.map(c => c.id === cat.id ? { ...c, active: !c.active } : c));
    } catch {
      toast.error("Error al actualizar estado");
    }
  };

  const downloadCsv = async () => {
    if (!tid) return;
    const res = await fetch("/api/categories/export", {
      credentials: "include",
      headers: clientRequestHeaders(tid),
    });
    if (!res.ok) {
      toast.error("Error al exportar");
      return;
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "categorias.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDelete = async () => {
    if (!deleteTarget || !tid) return;
    try {
      await apiDelete(`/api/categories?id=${deleteTarget.id}`, tid);
      toast.success("Categoría eliminada");
      setCats(prev => prev.filter(c => c.id !== deleteTarget.id));
      // Si por cualquier motivo la categoría borrada quedó abierta en el editor, no dejarla
      // ahí mostrando algo que ya no existe.
      if (editing?.id === deleteTarget.id) {
        setEditorOpen(false);
        setEditing(null);
      }
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : "Error al eliminar");
    } finally {
      setDeleteTarget(null);
    }
  };


  const columns: DataTableColumn<Categoria>[] = [
    {
      key: "name",
      header: "Nombre",
      render: (cat) => (
        <div className={`flex items-center gap-2 font-medium ${!cat.active ? "opacity-50" : ""}`}>
          {cat.imageUrl && <img src={cat.imageUrl} alt="" className="h-8 w-8 rounded object-cover" />}
          <span>{cat.name}</span>
        </div>
      ),
    },
    {
      key: "parent",
      header: "Padre",
      className: "hidden md:table-cell text-xs text-muted-foreground",
      render: (cat) => cat.parentName ?? "—",
    },
    {
      key: "description",
      header: "Descripción",
      className: "hidden sm:table-cell text-muted-foreground",
      render: (cat) => cat.description ?? <span className="italic">—</span>,
    },
    {
      key: "order",
      header: "Orden",
      className: "text-center text-muted-foreground",
      render: (cat) => cat.sortOrder,
    },
    {
      key: "active",
      header: "Activa",
      className: "text-center",
      render: (cat) => (
        <Switch checked={cat.active} onCheckedChange={() => handleToggleActive(cat)} aria-label="Activa" />
      ),
    },
    {
      key: "actions",
      header: "",
      className: "text-right",
      render: (cat) => (
        <div className="flex justify-end gap-1">
          <Button variant="ghost" size="icon" onClick={() => openEdit(cat)}>
            <Pencil className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="text-destructive hover:text-destructive"
            onClick={() => setDeleteTarget(cat)}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <CrudShellHeader
        title="Categorías"
        description="Organizá tus productos para que tus clientes encuentren todo más fácil."
        count={cats.length}
        backHref="/app/catalogacion"
        backLabel="Volver a Catalogación"
        action={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => void downloadCsv()}><Download className="mr-2 h-4 w-4" />Exportar CSV</Button>
            <Button onClick={openCreate}><Plus className="mr-2 h-4 w-4" />Nueva categoría</Button>
          </div>
        }
      />

      <ClinicalPageShell offset="none" className="space-y-6 pt-6">
        <CrudEditorShell
          open={editorOpen}
          onOpenChange={(open) => {
            setEditorOpen(open);
            if (!open) setEditing(null);
          }}
          title={editing ? "Editar categoría" : "Nueva categoría"}
          size="lg"
          list={
            loading ? (
              <Loading variant="list" />
            ) : (
              <DataTable
                columns={columns}
                rows={cats}
                getRowKey={(c) => c.id}
                onRowClick={(c) => openEdit(c)}
                emptyState={
                  <EmptyState
                    icon={Tag}
                    title="Sin categorías todavía"
                    description="Organizá tus productos en categorías para que tus clientes encuentren todo más fácil."
                    action={
                      <Button onClick={openCreate} variant="outline" size="sm" className="gap-2">
                        <Plus className="h-4 w-4" /> Crear primera categoría
                      </Button>
                    }
                  />
                }
              />
            )
          }
          footer={
            <>
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setEditorOpen(false);
                  setEditing(null);
                }}
                disabled={saving}
              >
                Cancelar
              </Button>
              <Button className="flex-1" onClick={() => void handleSave()} disabled={saving}>
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {editing ? "Guardar cambios" : "Crear categoría"}
              </Button>
            </>
          }
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="grid gap-1.5 sm:col-span-2">
              <FieldLabel htmlFor="cat-name" tipKey="cat.name" required>
                Nombre
              </FieldLabel>
              <Input
                id="cat-name"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder={
                  businessProfile === "fashion" ? "Ej: Remeras, Jeans, Accesorios" :
                  businessProfile === "bazar" ? "Ej: Cocina, Jardín, Decoración" :
                  businessProfile === "pharmacy" ? "Ej: Medicamentos, Higiene, Vitaminas" :
                  businessProfile === "pet" ? "Ej: Perros, Gatos, Aves" :
                  businessProfile === "catalog" ? "Ej: Infantil, Juvenil, Adultos" :
                  businessProfile === "digital_products" ? "Ej: Cursos, Templates, Ebooks" :
                  "Ej: Carnes, Lácteos, Verduras"
                }
                autoFocus
              />
            </div>
            <div className="grid gap-1.5 sm:col-span-2">
              <div className="flex items-center justify-between">
                <FieldLabel htmlFor="cat-desc" tipKey="cat.description">
                  Descripción
                </FieldLabel>
                <RenataLauncher
                  entityType="categoria"
                  entityId={editing?.id}
                  entityName={form.name}
                  field="description"
                  intent="write_description"
                  currentValue={form.description}
                  mode="icon"
                />
              </div>
              <Input
                id="cat-desc"
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                placeholder="Opcional"
              />
            </div>
            {editing && !form.imageUrl.trim() && tid ? (
              <div className="sm:col-span-2">
                <SmartMatchChip
                  tenantId={tid}
                  entityType="category"
                  entityId={editing.id}
                  slug={editing.slug}
                  name={form.name || editing.name}
                  onLinked={(url) => setForm((f) => ({ ...f, imageUrl: url }))}
                />
              </div>
            ) : null}
            <div className="sm:col-span-2 space-y-1.5">
              <FieldLabel tipKey="cat.image">Imagen de categoría</FieldLabel>
              <ImageUploadField
                kind="category"
                value={form.imageUrl.trim() || null}
                onChange={(url) => setForm((f) => ({ ...f, imageUrl: url ?? "" }))}
                tenantId={tid}
                disabled={saving}
                libraryLink={editing ? { entityType: "category", entityId: editing.id } : undefined}
              />
            </div>
            <div className="grid gap-1.5">
              <FieldLabel tipKey="cat.parent">Categoría principal</FieldLabel>
              <Select
                value={toOptionalSelectValue(form.parentId)}
                onValueChange={(v) =>
                  setForm((f) => ({ ...f, parentId: fromOptionalSelectValue(v) }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Raíz" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={OPTIONAL_SELECT_NONE}>Ninguna — esta es una categoría principal</SelectItem>
                  {cats
                    .filter((c) => c.id !== editing?.id && c.active)
                    .map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.parentName ? `${c.parentName} › ${c.name}` : c.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <FieldLabel htmlFor="cat-order" tipKey="cat.sortOrder">
                Orden
              </FieldLabel>
              <Input
                id="cat-order"
                type="number"
                min={0}
                value={form.sortOrder}
                onChange={(e) =>
                  setForm((f) => ({ ...f, sortOrder: parseInt(e.target.value) || 0 }))
                }
              />
            </div>

            {showCategoryExperience ? (
              <div className="sm:col-span-2 grid gap-3 rounded-lg border border-border/60 bg-muted/30 p-3">
                <div>
                  <p className="text-sm font-semibold">Cómo se ve en la tienda</p>
                  <p className="text-xs text-muted-foreground">
                    Elegí si esta categoría aparece en la página principal y si se destaca.
                  </p>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <label className="flex items-center justify-between gap-3 text-sm">
                    <FieldInlineHelp tipKey="cat.exp.showOnHome">Mostrar en la página principal</FieldInlineHelp>
                    <Switch
                      checked={form.experience.showOnHome}
                      onCheckedChange={(v) =>
                        setForm((f) => ({
                          ...f,
                          experience: {
                            ...f.experience,
                            showOnHome: v,
                            nav: { ...f.experience.nav, home: v },
                          },
                        }))
                      }
                    />
                  </label>
                  <label className="flex items-center justify-between gap-3 text-sm">
                    <FieldInlineHelp tipKey="cat.exp.featured">Categoría destacada</FieldInlineHelp>
                    <Switch
                      checked={form.experience.featured}
                      onCheckedChange={(v) =>
                        setForm((f) => ({
                          ...f,
                          experience: { ...f.experience, featured: v },
                        }))
                      }
                    />
                  </label>
                  <div className="grid gap-1.5">
                    <FieldLabel htmlFor="cat-home-order" tipKey="cat.exp.homeSortOrder">
                      Orden en la página principal
                    </FieldLabel>
                    <Input
                      id="cat-home-order"
                      type="number"
                      min={0}
                      value={form.experience.homeSortOrder}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          experience: {
                            ...f.experience,
                            homeSortOrder: parseInt(e.target.value) || 0,
                          },
                        }))
                      }
                    />
                  </div>
                  <label className="flex items-center justify-between gap-3 text-sm">
                    <FieldInlineHelp tipKey="cat.exp.showProductCount">
                      Mostrar cantidad de productos
                    </FieldInlineHelp>
                    <Switch
                      checked={form.experience.showProductCount}
                      onCheckedChange={(v) =>
                        setForm((f) => ({
                          ...f,
                          experience: { ...f.experience, showProductCount: v },
                        }))
                      }
                    />
                  </label>
                  <div className="grid gap-1.5">
                    <FieldLabel tipKey="cat.exp.badge">Insignia</FieldLabel>
                    <Select
                      value={form.experience.badge ?? OPTIONAL_SELECT_NONE}
                      onValueChange={(v) =>
                        setForm((f) => ({
                          ...f,
                          experience: {
                            ...f.experience,
                            badge:
                              v === OPTIONAL_SELECT_NONE
                                ? null
                                : (v as CategoryExperienceBadge),
                          },
                        }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Sin insignia" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={OPTIONAL_SELECT_NONE}>Sin insignia</SelectItem>
                        <SelectItem value="nuevo">Nuevo</SelectItem>
                        <SelectItem value="oferta">Oferta</SelectItem>
                        <SelectItem value="popular">Popular</SelectItem>
                        <SelectItem value="exclusivo">Exclusivo</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {showAccent ? (
                    <div className="grid gap-1.5">
                      <FieldLabel htmlFor="cat-accent" tipKey="cat.exp.accent">
                        Color de acento (opcional)
                      </FieldLabel>
                      <Input
                        id="cat-accent"
                        type="color"
                        value={form.experience.accentColor || "#16a34a"}
                        onChange={(e) =>
                          setForm((f) => ({
                            ...f,
                            experience: { ...f.experience, accentColor: e.target.value },
                          }))
                        }
                      />
                    </div>
                  ) : null}
                </div>
                <div>
                  <div className="mb-2">
                    <FieldLabel tipKey="cat.exp.nav" className="text-xs text-muted-foreground">
                      Mostrar en
                    </FieldLabel>
                  </div>
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                    {(
                      [
                        ["header", "Encabezado", "cat.exp.nav.header"],
                        ["megaMenu", "Menú de categorías", "cat.exp.nav.megaMenu"],
                        ["home", "Página principal", "cat.exp.nav.home"],
                        ["sidebar", "Menú lateral", "cat.exp.nav.sidebar"],
                        ["mobile", "En el celular", "cat.exp.nav.mobile"],
                        ["footer", "Pie de página", "cat.exp.nav.footer"],
                      ] as const
                    ).map(([key, label, tipKey]) => (
                      <label key={key} className="flex items-center gap-2 text-sm">
                        <Checkbox
                          checked={form.experience.nav[key]}
                          onCheckedChange={(v) =>
                            setForm((f) => ({
                              ...f,
                              experience: {
                                ...f.experience,
                                showOnHome:
                                  key === "home" ? Boolean(v) : f.experience.showOnHome,
                                nav: { ...f.experience.nav, [key]: Boolean(v) },
                              },
                            }))
                          }
                        />
                        <FieldInlineHelp tipKey={tipKey}>{label}</FieldInlineHelp>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </CrudEditorShell>
      </ClinicalPageShell>

      {/* Delete Confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={open => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar categoría?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará <strong>{deleteTarget?.name}</strong>. Los productos asociados quedarán sin categoría.
              Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleDelete}
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
